import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { formatBC } from "@/lib/format";
import {
  ShoppingBag, TrendingUp, Users, Wallet, Share2, Megaphone, GraduationCap,
  Bot, Package, Truck, Bell, ArrowUpRightSquare, Copy, CheckCircle2,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import MembershipGate from "@/components/MembershipGate";

/**
 * Commerce-focused partner dashboard. All investment-era mechanics
 * (deposits, locked assets, daily yield, ROI, harvest cycles) are removed.
 * The layout mirrors the CaaS journey: sell → track → get paid.
 */

function StatBlock({ label, amount, icon: Icon, accent = "white", testid, currency = true, sub }) {
  const accents = {
    white: "text-[#F3F4F6]",
    gold: "text-[#FBBF24]",
    emerald: "text-emerald-300",
    blue: "text-[#93C5FD]",
    crimson: "text-[#FCA5A5]",
  };
  return (
    <div className="glass-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white/50 font-semibold">
        <Icon size={14} /> {label}
      </div>
      <div className={`mt-3 font-mono-num text-2xl font-bold ${accents[accent]}`}>
        {currency ? `₱${formatBC(amount)}` : amount}
      </div>
      {sub && <div className="text-[11px] text-white/45 mt-1">{sub}</div>}
    </div>
  );
}

function QuickAction({ to, icon: Icon, label, sub, accent = "gold", testid }) {
  const map = {
    gold: "hover:border-[#FBBF24] hover:text-[#FBBF24]",
    blue: "hover:border-[#93C5FD] hover:text-[#93C5FD]",
    emerald: "hover:border-emerald-300 hover:text-emerald-300",
  };
  return (
    <Link
      to={to}
      className={`glass-card p-4 flex items-start gap-3 border border-white/10 transition-colors ${map[accent]}`}
      data-testid={testid}
    >
      <Icon size={22} className="mt-0.5 text-[#FBBF24] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-display font-black text-sm text-white truncate">{label}</div>
        <div className="text-[11px] text-white/50 mt-0.5 leading-snug">{sub}</div>
      </div>
      <ArrowUpRightSquare size={14} className="text-white/40 shrink-0 mt-1" />
    </Link>
  );
}

/** Aggregate the partner's own recent orders into headline metrics. */
function useOrderMetrics() {
  const { data: orders = [] } = useQuery({
    queryKey: ["my-orders"],
    queryFn: async () => (await api.get("/orders/mine")).data,
    staleTime: 30_000,
  });
  return useMemo(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    let salesToday = 0;
    let ordersToday = 0;
    let ordersAll = orders.length;
    const customers = new Set();
    let inTransit = 0;
    for (const o of orders) {
      customers.add(o.user_email);
      if ((o.placed_at || "").startsWith(todayIso)) {
        ordersToday += 1;
        salesToday += Number(o.total_bc || 0);
      }
      if (o.status === "shipped" || o.status === "paid") inTransit += 1;
    }
    return { salesToday, ordersToday, ordersAll, customers: customers.size, inTransit };
  }, [orders]);
}

/** Partner referral / affiliate stats (uses the existing affiliate endpoint). */
function useAffiliateStats() {
  const { data } = useQuery({
    queryKey: ["affiliate-stats"],
    queryFn: async () => (await api.get("/affiliate/me").catch(() => ({ data: null }))).data,
    staleTime: 60_000,
  });
  return data || {};
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const metrics = useOrderMetrics();
  const affiliate = useAffiliateStats();
  const [copied, setCopied] = useState(false);

  useEffect(() => { refresh(); }, [refresh]);

  const affiliateCode = user?.affiliate_code || affiliate?.affiliate_code || "";
  const shareLink = affiliateCode
    ? `${window.location.origin}/?ref=${affiliateCode}`
    : `${window.location.origin}/`;

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success("Affiliate link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Clipboard unavailable — copy the link manually.");
    }
  };

  const walletBalance = Number(user?.active_balance || 0);
  const retailProfit = Number(affiliate?.retail_profit_total_bc || 0);
  const referralIncome = Number(affiliate?.referral_income_total_bc || affiliate?.pending_bc || 0);
  const teamSales = Number(affiliate?.team_sales_total_bc || 0);

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      <MembershipGate />

      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">Partner Dashboard</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight" data-testid="dashboard-greeting">
            Welcome back, {user?.full_name?.split(" ")[0] || "Partner"}.
          </h1>
          <p className="text-white/60 mt-1 text-sm">Your commerce headquarters — sales, orders, and payouts at a glance.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/shop"
            className="text-xs px-3 py-2 rounded-md bg-white/5 border border-white/10 hover:border-[#FBBF24] hover:text-[#FBBF24] inline-flex items-center gap-1"
            data-testid="cta-shop"
          >
            <ShoppingBag size={13} /> Browse Products
          </Link>
          <Link
            to="/earnings"
            className="text-xs px-3 py-2 rounded-md bg-[#FBBF24] text-[#0F2042] font-bold hover:bg-yellow-300 inline-flex items-center gap-1"
            data-testid="cta-withdraw"
          >
            <Wallet size={13} /> Withdraw Earnings
          </Link>
        </div>
      </div>

      {/* Primary Commission Wallet card */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card gold-border-glow gold-shimmer p-8 relative overflow-hidden"
        data-testid="commission-wallet-card"
      >
        <div className="absolute inset-0 zfd-grid-lines opacity-30 pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#FBBF24] font-bold">Commission Wallet</div>
            <div className="mt-2 font-mono-num text-5xl md:text-6xl font-bold text-[#FBBF24]" data-testid="commission-wallet-balance">
              ₱{formatBC(walletBalance)}
            </div>
            <div className="mt-1 text-xs text-white/50">Available for withdrawal · Commission Credits</div>
          </div>
          <div className="flex flex-col gap-3 min-w-[260px]">
            <button
              onClick={copyShareLink}
              className="w-full px-4 py-2.5 rounded-md bg-white/5 border border-white/10 hover:border-[#FBBF24] hover:text-[#FBBF24] text-sm font-bold uppercase tracking-widest inline-flex items-center justify-center gap-2"
              data-testid="copy-share-link"
            >
              {copied ? <CheckCircle2 size={14} className="text-emerald-300" /> : <Copy size={14} />}
              {copied ? "Copied!" : "Copy Affiliate Link"}
            </button>
            <Link
              to="/earnings"
              className="w-full px-4 py-2.5 rounded-md bg-[#FBBF24] text-[#0F2042] font-bold uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 hover:bg-yellow-300"
              data-testid="withdraw-btn"
            >
              <ArrowUpRightSquare size={14} /> Withdraw
            </Link>
          </div>
        </div>
      </motion.div>

      {/* Row 1 — sales metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="metrics-primary">
        <StatBlock icon={TrendingUp} label="Sales Today" amount={metrics.salesToday} accent="gold" testid="metric-sales-today" sub={`${metrics.ordersToday} order${metrics.ordersToday === 1 ? "" : "s"} placed`} />
        <StatBlock icon={ShoppingBag} label="Total Orders" amount={metrics.ordersAll} accent="white" currency={false} testid="metric-orders-all" sub={`${metrics.inTransit} in transit`} />
        <StatBlock icon={Users} label="Customers" amount={metrics.customers} accent="blue" currency={false} testid="metric-customers" sub="unique buyers" />
        <StatBlock icon={Truck} label="Shipping Status" amount={metrics.inTransit} accent="emerald" currency={false} testid="metric-shipping" sub="orders in transit" />
      </div>

      {/* Row 2 — commission breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="metrics-commission">
        <StatBlock icon={Wallet} label="Commission Wallet" amount={walletBalance} accent="gold" testid="metric-wallet" sub="withdraw anytime" />
        <StatBlock icon={TrendingUp} label="Retail Profit" amount={retailProfit} accent="emerald" testid="metric-retail-profit" sub="all-time" />
        <StatBlock icon={Share2} label="Referral Income" amount={referralIncome} accent="blue" testid="metric-referral" sub="from your team" />
        <StatBlock icon={Users} label="Team Sales" amount={teamSales} accent="white" testid="metric-team-sales" sub="downline volume" />
      </div>

      {/* Quick actions grid */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold mb-3">Quick Actions</div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="quick-actions">
          <QuickAction to="/shop" icon={ShoppingBag} label="Browse Products" sub="Find your next hero SKU to promote" testid="qa-shop" />
          <QuickAction to="/shop/orders" icon={Package} label="My Orders" sub="Track every customer order in real time" testid="qa-orders" />
          <QuickAction to="/rewards" icon={Share2} label="Partner Rewards" sub="Retail profit, referrals, team overrides" testid="qa-rewards" accent="blue" />
          <QuickAction to="/marketing" icon={Megaphone} label="Marketing Center" sub="AI captions, video scripts, share links" testid="qa-marketing" />
          <QuickAction to="/earnings" icon={Wallet} label="Commission Wallet" sub="Withdraw commissions to your bank" testid="qa-earnings" accent="emerald" />
          <QuickAction to="/inbox" icon={Bot} label="AI Assistant & Support" sub="Talk to Zen — your 24/7 AI coach" testid="qa-ai" accent="blue" />
        </div>
      </div>

      {/* Notifications / next steps */}
      <div className="glass-card p-6" data-testid="notifications-panel">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#FBBF24] font-bold mb-3">
          <Bell size={12} /> Recommended Next Steps
        </div>
        <ul className="space-y-2 text-sm text-white/80">
          <li className="flex items-start gap-2">
            <CheckCircle2 size={14} className="text-[#FBBF24] mt-0.5 shrink-0" />
            Post your affiliate link to social media — every share increases customer discovery.
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 size={14} className="text-[#FBBF24] mt-0.5 shrink-0" />
            Use the Marketing Center to generate a caption for your top product — takes 30 seconds.
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 size={14} className="text-[#FBBF24] mt-0.5 shrink-0" />
            Complete Partner Verification to unlock priority payouts.
          </li>
          <li className="flex items-start gap-2">
            <GraduationCap size={14} className="text-[#FBBF24] mt-0.5 shrink-0" />
            New to selling online? Message Support to book your onboarding call.
          </li>
        </ul>
      </div>
    </div>
  );
}
